import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient.ts";

const getImageUrl = (path) =>
  `https://aujdasturxpqlzqjbztf.supabase.co/storage/v1/object/public/animalia/${path}`;

function CatManager() {
  const [categories, setCategories] = useState([]);
  const [petTypes, setPetTypes] = useState([]);
  const [form, setForm] = useState({ name: "", image: "", pet_type: "" });
  const [imageFile, setImageFile] = useState(null);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // New filter state
  const [filterType, setFilterType] = useState("");

  useEffect(() => {
    fetchCategories();
    fetchPetTypes();
  }, []);

  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .order("pet_type", { ascending: true })
      .order("order", { ascending: true });

    if (error) {
      console.error("Erreur lors du chargement des catégories :", error);
      setCategories([]);
    } else {
      setCategories(Array.isArray(data) ? data : []);
    }
  };

  const fetchPetTypes = async () => {
    const { data, error } = await supabase.from("categories").select("pet_type");
    if (error) {
      console.error("Erreur lors du chargement des types d'animaux :", error);
      setPetTypes([]);
    } else {
      if (Array.isArray(data)) {
        const uniquePetTypes = [...new Set(data.map((pt) => pt.pet_type))];
        setPetTypes(uniquePetTypes);
      } else {
        setPetTypes([]);
      }
    }
  };

  const uploadImage = async (file) => {
    const fileExt = file.name.split(".").pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `categories/${fileName}`;

    const { error } = await supabase.storage
      .from("animalia")
      .upload(filePath, file);

    if (error) {
      console.error("Erreur lors de l'upload de l'image :", error.message);
      return null;
    }

    return getImageUrl(filePath);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let imageUrl = form.image;

    if (imageFile) {
      const uploadedUrl = await uploadImage(imageFile);
      if (uploadedUrl) {
        imageUrl = uploadedUrl;
      } else {
        setLoading(false);
        return;
      }
    }

    if (editId) {
      const { error } = await supabase
        .from("categories")
        .update({ name: form.name, image: imageUrl, pet_type: form.pet_type })
        .eq("id", editId);
      if (error) console.error("Erreur lors de la modification :", error);
    } else {
      const { error } = await supabase.from("categories").insert([
        {
          name: form.name,
          image: imageUrl,
          pet_type: form.pet_type,
        },
      ]);
      if (error) console.error("Erreur lors de l'ajout :", error);
    }

    setForm({ name: "", image: "", pet_type: "" });
    setImageFile(null);
    setEditId(null);
    setLoading(false);
    fetchCategories();
  };

  const handleEdit = (category) => {
    setForm({
      name: category.name,
      image: category.image,
      pet_type: category.pet_type || "",
    });
    setEditId(category.id);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm({ name: "", image: "", pet_type: "" });
    setImageFile(null);
    setEditId(null);
  };

  const handleDelete = async (id) => {
    // French confirmation dialog
    const confirmed = window.confirm(
      "Êtes-vous sûr de vouloir supprimer cette catégorie ? Cette action est irréversible !"
    );

    if (!confirmed) return; // exit if user cancels

    // Proceed to delete
    const { error } = await supabase.from("categories").delete().eq("id", id);
    if (error) {
      console.error("Erreur lors de la suppression :", error);
      alert("Une erreur est survenue lors de la suppression de la catégorie.");
    } else {
      alert("Catégorie supprimée avec succès !");
      fetchCategories(); // refresh list
    }
  };

  // Filter categories by selected filterType or show all
  const handleOrderChange = async (categoryId, newOrder) => {
    try {
      const { data, error } = await supabase
        .from("categories")
        .update({ order: newOrder })
        .eq("id", categoryId)
        .select(); // <- return updated row so we can check

      if (error) {
        console.error("Erreur lors de la mise à jour de l'ordre :", error);
      } else {
        console.log("Mise à jour réussie :", data);

        // Update locally
        setCategories((prev) =>
          prev.map((cat) =>
            cat.id === categoryId ? { ...cat, order: newOrder } : cat
          )
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  const filteredCategories = filterType
    ? categories.filter((cat) => cat.pet_type === filterType)
    : categories;


  return (
    <div className="container py-5">
      <h2 className="mb-4">Gestion des catégories</h2>

      <form onSubmit={handleSubmit} className="mb-4">
        {/* Your form inputs here */}
        <div className="mb-3">
          <label className="form-label">Nom de la catégorie</label>
          <input
            type="text"
            className="form-control"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Type d'animal</label>
          <select
            className="form-select"
            value={form.pet_type}
            onChange={(e) => setForm({ ...form, pet_type: e.target.value })}
            required
          >
            <option value="" disabled>
              Sélectionnez un type d'animal
            </option>
            {petTypes.map((type) => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>
        </div>


        <div className="mb-3">
          <label className="form-label">Fichier image</label>
          <input
            type="file"
            className="form-control"
            accept="image/*"
            onChange={(e) => setImageFile(e.target.files?.[0] || null)}
          />
        </div>

        {form.image && !imageFile && (
          <div className="mb-3">
            <img
              src={form.image}
              alt="aperçu"
              style={{ height: "100px", objectFit: "cover" }}
            />
          </div>
        )}

        <button type="submit" className="btn btn-secondary" disabled={loading}>
          {editId ? "Modifier la catégorie" : "Ajouter une catégorie"}
        </button>
        {editId && (
          <button
            type="button"
            className="btn btn-secondary ms-2"
            onClick={() => {
              setForm({ name: "", image: "", pet_type: "" });
              setImageFile(null);
              setEditId(null);
            }}
          >
            Annuler
          </button>
        )}
      </form>

      {/* FILTERS */}
      <div className="mb-4">
        {/* <span className="me-3">Filtrer par type d'animal:</span> */}
        <button
          className={`btn btn-l me-2 ${filterType === "" ? "btn-primary" : "btn-outline-primary"
            }`}
          onClick={() => setFilterType("")}
        >
          Tous
        </button>
        {petTypes.map((type) => (
          <button
            key={type}
            className={`btn btn-l me-2 ${filterType === type ? "btn-primary" : "btn-outline-primary"
              }`}
            onClick={() => setFilterType(type)}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>

      {/* CATEGORIES LIST */}
      <h4>Liste des catégories</h4>
      <div className="row">
        {filteredCategories.length > 0 ? (
          filteredCategories.map((cat) => (
            <div
              key={cat.id}
              className="col-md-5 mb-3" // wider columns
            >
              <div className="card h-100 shadow-sm" style={{ minHeight: "350px" }}>
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="card-img-top"
                  style={{ height: "220px", objectFit: "cover" }} // taller image
                />
                <div className="card-body">
                  <h5 className="card-title">{cat.name}</h5>
                  <p className="card-text fst-italic text-muted mt-5">
                    Type: {cat.pet_type}
                  </p>



                  <div className="mb-2 d-flex align-items-center gap-2">
                    <label style={{ marginBottom: 0, fontWeight: "500", fontSize: "14px" }}>
                      Ordre:
                    </label>
                    <input
                      type="number"
                      value={cat.order ?? 0}
                      onChange={(e) =>
                        setCategories((prev) =>
                          prev.map((c) =>
                            c.id === cat.id ? { ...c, order: Number(e.target.value) } : c
                          )
                        )
                      }
                      onBlur={(e) => handleOrderChange(cat.id, Number(e.target.value))}
                      style={{
                        width: "70px",
                        padding: "6px 10px",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                        textAlign: "center",
                        fontSize: "14px",
                        outline: "none",
                        transition: "all 0.2s ease-in-out",
                      }}
                      onFocus={(e) =>
                        (e.target.style.border = "1px solid #007bff")
                      }
                      onBlurCapture={(e) =>
                        (e.target.style.border = "1px solid #ccc")
                      }
                    />
                  </div>

                  <div className="d-flex justify-content-between mt-3">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleEdit(cat)}
                    >
                      Modifier
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(cat.id)}
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p>Aucune catégorie trouvée.</p>
        )}
      </div>


    </div>
  );
}

export default CatManager;
